import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';

import { combineLatest, fromEvent, map, merge, of, startWith, switchMap, timer } from 'rxjs';

import { SignalRService } from '@core/services/signalr.service';

/** Navigations faster than this show no bar at all, so quick page changes do not flicker. */
const NAVIGATION_BAR_DELAY_MS = 150;
/** A connection problem must last this long before it is announced (short reconnects heal alone). */
const CONNECTION_NOTICE_DELAY_MS = 2000;

type ConnectionProblem = 'offline' | 'reconnecting' | 'lost';

/**
 * Two app-wide signals the pages cannot give on their own:
 *  - a thin progress bar while a route loads (lazy chunk, guards that wait for the profile),
 *    otherwise the area next to the nav stays blank and the click looks lost;
 *  - a notice when the live connection (SignalR) is gone or the browser is offline. Every page
 *    relies on events to show confirmed state, so without the notice stale numbers look current.
 */
@Component({
  selector: 'app-status-bar',
  template: `
    @if (navigating()) {
      <div class="nav-progress" role="progressbar" aria-label="Seite wird geladen"><span></span></div>
    }
    @if (problem(); as p) {
      <div class="connection-notice" [class.offline]="p === 'offline'" role="status">
        @switch (p) {
          @case ('offline') {
            <span>Keine Internetverbindung. Eingaben können gerade nicht gespeichert werden.</span>
          }
          @case ('reconnecting') {
            <span>Verbindung wird wiederhergestellt. Angezeigte Daten sind evtl. nicht aktuell.</span>
          }
          @case ('lost') {
            <span>Live-Aktualisierung unterbrochen. Angezeigte Daten sind evtl. nicht aktuell.</span>
            <button type="button" [disabled]="retrying()" [attr.aria-busy]="retrying() || null" (click)="retry()">
              {{ retrying() ? 'Verbinde…' : 'Neu verbinden' }}
            </button>
          }
        }
      </div>
    }
  `,
  styles: `
    .nav-progress {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      z-index: 1300;
      overflow: hidden;
      background: var(--color-primary-lighter);

      span {
        display: block;
        height: 100%;
        width: 40%;
        background: var(--color-accent-dark);
        animation: nav-progress-slide 1.1s ease-in-out infinite;
      }
    }

    @keyframes nav-progress-slide {
      from {
        transform: translateX(-100%);
      }

      to {
        transform: translateX(250%);
      }
    }

    .connection-notice {
      position: fixed;
      top: var(--s-3);
      left: 50%;
      transform: translateX(-50%);
      z-index: 1200;
      display: flex;
      align-items: center;
      gap: var(--s-3);
      max-width: calc(100vw - 2 * var(--s-4));
      padding: var(--s-2) var(--s-4);
      border-radius: var(--r-full);
      font-size: 13px;
      background: var(--color-warning-bg);
      color: var(--color-warning-text);
      border: 1px solid var(--color-warning);
      box-shadow: var(--shadow-md);

      &.offline {
        background: var(--color-error-bg);
        color: var(--color-error);
        border-color: var(--color-error);
      }

      button {
        flex-shrink: 0;
        font-size: 13px;
        text-decoration: underline;
        color: inherit;

        &:disabled {
          opacity: .6;
          cursor: progress;
        }
      }
    }

    @media (max-width: 768px) {
      /* Below the mobile top bar of the nav. */
      .connection-notice {
        top: 64px;
        border-radius: var(--r-md);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .nav-progress span {
        animation-duration: 3s;
      }
    }
  `,
})
export class StatusBar {
  private readonly router = inject(Router);
  private readonly signalR = inject(SignalRService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly navigating = signal(false);
  protected readonly retrying = signal(false);

  private wasConnected = false;

  protected readonly problem = toSignal(
    combineLatest([
      this.signalR.state$,
      merge(fromEvent(window, 'online'), fromEvent(window, 'offline')).pipe(
        map(() => navigator.onLine),
        startWith(navigator.onLine),
      ),
    ]).pipe(
      map(([state, online]): ConnectionProblem | null => {
        if (state === 'connected') { this.wasConnected = true; }
        if (!online) {return 'offline';}
        if (state === 'error') {return 'lost';}
        // Before the first successful connect 'disconnected'/'connecting' is just startup (or mock mode).
        if (!this.wasConnected) {return null;}
        if (state === 'connecting') {return 'reconnecting';}
        return state === 'disconnected' ? 'lost' : null;
      }),
      switchMap((problem) => (problem ? timer(CONNECTION_NOTICE_DELAY_MS).pipe(map(() => problem), startWith(null)) : of(null))),
    ),
    { initialValue: null },
  );

  constructor() {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        timeout = setTimeout(() => this.navigating.set(true), NAVIGATION_BAR_DELAY_MS);
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        clearTimeout(timeout);
        this.navigating.set(false);
      }
    });
    this.destroyRef.onDestroy(() => clearTimeout(timeout));
  }

  protected async retry(): Promise<void> {
    this.retrying.set(true);
    try {
      await this.signalR.reconnect();
    } catch {
      // The state stays 'error'/'disconnected', so the notice remains; the console has the cause.
    } finally {
      this.retrying.set(false);
    }
  }
}
